import React, { useState } from 'react';

const DataImportExportView = ({ dataService, onDataChange, supportsFileSystemAccess }) => {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingTemplate, setExportingTemplate] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.yaml') && !file.name.endsWith('.yml')) {
      alert('Please upload a YAML file');
      return;
    }

    setImporting(true);
    try {
      const content = await file.text();
      const files = { [file.name]: content };
      await dataService.importData(files);
      await onDataChange();
      alert('Data imported successfully!');
    } catch (error) {
      console.error('Error importing file:', error);
      alert('Error importing file: ' + error.message);
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const files = await dataService.exportData();
      
      for (const [fileName, content] of Object.entries(files)) {
        const blob = new Blob([content], { type: 'application/x-yaml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      alert('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportSkillsTemplate = async () => {
    setExportingTemplate(true);
    try {
      const skillsData = await dataService.loadSkills();
      const skills = skillsData.skills || [];
      
      // Collect all certifications from skills.yaml (each skill can have related_certifications)
      const allCertifications = new Set();
      skills.forEach(skill => {
        if (skill.related_certifications && Array.isArray(skill.related_certifications)) {
          skill.related_certifications.forEach(cert => {
            allCertifications.add(cert);
          });
        }
      });
      
      // Create CSV with Type, Skills/Certifications, and people names in header row
      const csvRows = [];
      
      // Header row: Type, Skills/Certifications, Person 1, Person 2, Person 3, etc.
      csvRows.push('Type,Skills/Certifications,Person 1,Person 2,Person 3,Person 4,Person 5');
      
      // Add skills rows with skill IDs from skills.yaml
      skills.forEach(skill => {
        csvRows.push(`SKILL,"${skill.id}",,,,,`);
      });
      
      // Add certifications rows with exact names (from related_certifications in skills.yaml)
      Array.from(allCertifications).forEach(cert => {
        csvRows.push(`CERT,"${cert}",,,,,`);
      });
      
      const csvContent = csvRows.join('\n');
      
      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'skills-template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('Skills template exported successfully!');
    } catch (error) {
      console.error('Error exporting skills template:', error);
      alert('Error exporting skills template: ' + error.message);
    } finally {
      setExportingTemplate(false);
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setImportingCsv(true);
    try {
      const content = await file.text();
      const lines = content.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('CSV must have at least a header row and one data row');
      }
      
      // Detect delimiter (comma or semicolon) and parse header row (people names)
      const delimiter = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].split(delimiter).map(h => h.trim().replace(/"/g, ''));
      
      // Check if this is the new format with Type column (column 0: Type, column 1: Skills/Certifications)
      const hasTypeColumn = headers[0].toLowerCase() === 'type' && headers[1].toLowerCase() === 'skills/certifications';
      
      // Support both old format (Skills/Certifications in column 0) and new format (Type in column 0)
      if (!hasTypeColumn && headers[0].toLowerCase() !== 'skills/certifications') {
        throw new Error('First column must be "Type" (new format) or "Skills/Certifications" (old format)');
      }
      
      const skillsData = await dataService.loadSkills();
      const existingSkills = skillsData.skills || [];
      
      // Collect all certifications from skills.yaml (each skill can have related_certifications)
      const allCertifications = new Set();
      existingSkills.forEach(skill => {
        if (skill.related_certifications && Array.isArray(skill.related_certifications)) {
          skill.related_certifications.forEach(cert => {
            allCertifications.add(cert);
          });
        }
      });
      
      // Create people objects from the appropriate columns based on format
      const people = {};
      const peopleStartIndex = hasTypeColumn ? 2 : 1; // Skip Type column if present
      
      for (let colIndex = peopleStartIndex; colIndex < headers.length; colIndex++) {
        const personName = headers[colIndex].trim();
        if (personName && !personName.toLowerCase().includes('person')) {
          people[personName] = {
            name: personName,
            skills: [],
            certifications: []
          };
        }
      }
      
      // Process each skill/certification row
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i].split(delimiter).map(cell => cell.trim().replace(/"/g, ''));
        
        if (row.length < (hasTypeColumn ? 3 : 2)) continue; // Need at least Type + Name + one person column
        
        let skillOrCertName, isCertification;
        
        if (hasTypeColumn) {
          // New format: Type in column 0, Name in column 1
          const typeValue = row[0].trim().toLowerCase();
          skillOrCertName = row[1].trim();
          isCertification = typeValue === 'cert' || typeValue === 'certification';
        } else {
          // Old format: Check for prefixes or fallback to lookup
          skillOrCertName = row[0].trim();
          
          const isSkill = skillOrCertName.startsWith('[SKILL] ');
          isCertification = skillOrCertName.startsWith('[CERT] ');
          
          if (isSkill) {
            skillOrCertName = skillOrCertName.replace('[SKILL] ', '');
          } else if (isCertification) {
            skillOrCertName = skillOrCertName.replace('[CERT] ', '');
          } else {
            // Fallback: check if this is a certification by looking it up in our collected certifications
            const fallbackIsCertification = allCertifications.has(skillOrCertName);
            if (fallbackIsCertification) {
              isCertification = true;
            }
          }
        }
        
        const cleanName = skillOrCertName;
        if (!cleanName) continue;
        
        // Check each person column for 'x' marks
        for (let colIndex = peopleStartIndex; colIndex < row.length && colIndex < headers.length; colIndex++) {
          const personName = headers[colIndex].trim();
          const cellValue = row[colIndex].toLowerCase().trim();
          
          if ((cellValue === 'x' || cellValue === 'yes' || cellValue === '1' || cellValue === 'true') && people[personName]) {
            if (isCertification) {
              // Find matching certification from skills.yaml related_certifications
              if (allCertifications.has(cleanName)) {
                people[personName].certifications.push(cleanName);
              }
            } else {
              // Find matching skill by ID
              const matchingSkill = existingSkills.find(skill => 
                skill.id === cleanName
              );
              if (matchingSkill) {
                people[personName].skills.push(cleanName);
              }
            }
          }
        }
      }
      
      // Save all people
      let createdCount = 0;
      for (const person of Object.values(people)) {
        if (person.name && !person.name.toLowerCase().includes('person')) {
          await dataService.savePerson(person);
          createdCount++;
        }
      }
      
      await onDataChange();
      alert(`CSV imported successfully! Created ${createdCount} people records.`);
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Error importing CSV: ' + error.message);
    } finally {
      setImportingCsv(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Show YAML import/export only for browsers without File System Access API */}
      {!supportsFileSystemAccess && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1 md:flex md:justify-between">
                <p className="text-sm text-blue-700">
                  Your browser doesn't support the File System Access API. Use the import/export features below to manage your data.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">YAML Data Management</h3>
              <p className="mt-1 text-sm text-gray-500">
                Import and export your skills data as YAML files
              </p>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Import Data</h4>
                <div className="flex items-center space-x-4">
                  <label className="relative cursor-pointer bg-white rounded-md border border-gray-300 shadow-sm py-2 px-3 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                    <span className="block text-sm text-gray-700">
                      {importing ? 'Importing...' : 'Choose YAML file'}
                    </span>
                    <input
                      type="file"
                      accept=".yaml,.yml"
                      onChange={handleFileUpload}
                      disabled={importing}
                      className="sr-only"
                    />
                  </label>
                  {importing && (
                    <div className="flex items-center text-sm text-blue-600">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Upload capabilities.yaml, skills.yaml, or individual person files
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Export Data</h4>
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      Download All Data
                    </>
                  )}
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  Downloads all your data as separate YAML files
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CSV Skills Template - Always visible */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Team Skills Mapping</h3>
          <p className="mt-1 text-sm text-gray-500">
            Use CSV templates for easy team skills collection and bulk import
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <button
              onClick={handleExportSkillsTemplate}
              disabled={exportingTemplate}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingTemplate ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Skills Template
                </>
              )}
            </button>
            <p className="mt-2 text-xs text-gray-500">
              Downloads a CSV template with all skills as columns - perfect for team skills mapping
            </p>
          </div>

          <div>
            <div className="flex items-center space-x-4">
              <label className="relative cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {importingCsv ? 'Importing CSV...' : 'Upload Filled Skills CSV'}
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  disabled={importingCsv}
                  className="sr-only"
                />
              </label>
              {importingCsv && (
                <div className="flex items-center text-sm text-green-600">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing CSV...
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Upload the filled CSV template to automatically create people records with skills and certifications
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataImportExportView;